import { LogOut, ShieldAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useMatches } from 'react-router';
import { BrandLogo } from '../components/brand/BrandLogo.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Card } from '../components/ui/Card.tsx';
import { Drawer } from '../components/ui/Drawer.tsx';
import { EmptyState } from '../components/ui/States.tsx';
import { ChatAlerts } from '../features/chat-alerts/ChatAlerts.tsx';
import { useAuth } from '../lib/auth.tsx';
import { cx } from '../lib/cx.ts';
import { MEDIA, useDocumentTitle, useLocalStorageState, useMediaQuery } from '../lib/hooks.ts';
import { visibleNavGroups } from '../lib/permissions.ts';
import { BottomNav } from './BottomNav.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { NAV_GROUPS, type RouteHandle } from './nav.ts';
import { PageMetaProvider } from './pageMeta.tsx';
import { SectionTabs } from './SectionTabs.tsx';
import { SidebarNav } from './Sidebar.tsx';
import { Topbar } from './Topbar.tsx';
import './shell.css';

export const NAV_DRAWER_ID = 'nav-drawer';

function useRouteHandle(): RouteHandle | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as RouteHandle | undefined;
    if (handle?.title) return handle;
  }
  return undefined;
}

/** DESIGN.md §6: sidebar + topbar + content canvas, responsive per §15. */
export function AppShell() {
  const { me, logout } = useAuth();
  const location = useLocation();
  const handle = useRouteHandle();
  useDocumentTitle(handle?.title);

  const isDesktop = useMediaQuery(MEDIA.desktop);
  const isWide = useMediaQuery(MEDIA.wide);
  const isPhone = useMediaQuery(MEDIA.phone);
  const isTablet = !isDesktop && !isPhone;
  // Collapsed (icons only) is remembered separately for mid-size and wide screens: mid starts collapsed, wide starts open.
  const [railMid, setRailMid] = useLocalStorageState('natashu.sidebar.rail', true);
  const [railWide, setRailWide] = useLocalStorageState('natashu.sidebar.rail.wide', false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Computer: collapse/expand in place. iPad (768–1023px): always the icon rail; "ขยายเมนู" opens the full menu drawer.
  // Phone: bottom bar + drawer.
  const rail = isTablet || (isDesktop && (isWide ? railWide : railMid));
  const toggleRail = isTablet ? () => setDrawerOpen(true) : isWide ? () => setRailWide((v) => !v) : () => setRailMid((v) => !v);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (me?.user.mustChangePassword) {
    return (
      <div className="forced-layout">
        <header className="forced-layout__header">
          <BrandLogo size={36} />
          <span className="forced-layout__name">NATASHU Chat &amp; CRM</span>
          <Button variant="ghost" icon={LogOut} onClick={() => void logout()} className="forced-layout__logout">
            ออกจากระบบ
          </Button>
        </header>
        <main id="main" className="forced-layout__main">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <PageMetaProvider routeTitle={handle?.title} routeGroup={handle?.group}>
    <div className={cx('app-shell', rail && 'is-rail')}>
      <a className="skip-link" href="#main">
        ข้ามไปยังเนื้อหา
      </a>
      {(isDesktop || isTablet) && (
        <aside className="sidebar" aria-label="แถบเมนู">
          <SidebarNav rail={rail} onToggleRail={toggleRail} />
        </aside>
      )}
      {!isDesktop && (
        <Drawer
          id={NAV_DRAWER_ID}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="เมนูหลัก"
          side="left"
          width={288}
          sheetOnPhone={false}
          hideHeader
          className="nav-drawer"
        >
          <SidebarNav rail={false} onClose={() => setDrawerOpen(false)} />
        </Drawer>
      )}
      <div className="app-main">
        <Topbar
          handle={handle}
          showMenuButton={isPhone}
          menuExpanded={drawerOpen}
          onOpenMenu={() => setDrawerOpen(true)}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        <main id="main" className="page-canvas" tabIndex={-1}>
          <SectionTabs section={handle?.section} />
          <PageGate handle={handle}>
            {/* Keyed by module so sub-routes (e.g. /inbox/:id) do not remount the page. */}
            <div key={location.pathname.split('/')[1] ?? ''} className="page-enter">
              <Outlet />
            </div>
          </PageGate>
        </main>
      </div>
      {isPhone && <BottomNav moreExpanded={drawerOpen} onOpenMore={() => setDrawerOpen(true)} />}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ChatAlerts />
    </div>
    </PageMetaProvider>
  );
}

/** Hides pages the user has no permission for (the API rejects the data anyway). */
function PageGate({ handle, children }: { handle: RouteHandle | undefined; children: ReactNode }) {
  const { can, permissions } = useAuth();
  const location = useLocation();
  if (!handle?.anyOf || can(handle.anyOf)) return <>{children}</>;

  if (location.pathname === '/') {
    const first = visibleNavGroups(NAV_GROUPS, permissions)[0]?.items[0];
    if (first && first.to !== '/') return <Navigate to={first.to} replace />;
  }
  return (
    <Card>
      <EmptyState
        icon={ShieldAlert}
        title="คุณไม่มีสิทธิ์เข้าถึงหน้านี้"
        description="หากต้องใช้งานส่วนนี้ กรุณาติดต่อเจ้าของระบบเพื่อขอสิทธิ์เพิ่มเติม"
        action={
          <Link to="/" className="btn btn--secondary">
            กลับหน้าแรก
          </Link>
        }
      />
    </Card>
  );
}
